import { Injectable } from '@angular/core';
import { FormScriptingField } from '../../classes/form-scripting-field';
import { CollectionFormService } from '../collection-form.service';
import { CollectionForm } from '../../../../../../models/ts/collection-form.model';
import { EvaluationActionFieldValueDto } from '../../../../../../models/ts/evaluation-action-field-value-dto.model';
import { EvaluationsActionsType } from '../../../../../../models/ts/evaluations-actions-type.model';
import { CollectionFormField } from '../../../../../../models/ts/collection-form-field.model';
import { ProtectedFieldType } from '../../../../../../models/ts/protected-field-type.model';
import { LinkedCollectionStorageType } from '../../../../../../models/ts/linked-collection-storage-type.model';
import { EvaluationActionDto } from '../../../../../../models/ts/evaluation-action-dto.model';

export interface ScriptingAction {
  formId: string;
  fieldId: number;
  property: 'IsHidden' | 'IsRequired';
  value: boolean;
}

/**
 * Service handling execution of scripting actions
 */
@Injectable({
  providedIn: 'root'
})
export class FormScriptingActionsService {

  private static getSourceCollectionFields(form: CollectionForm, sourceCollectionsID: number, viewDataSourcesID: number): CollectionFormField[] {
    const collectionFields: CollectionFormField[] = [];

    CollectionFormService.extractFieldsFromForm(form).forEach(field => {
      if (field.SourceCollectionsID == sourceCollectionsID && field.DataDesignViewDataSourcesID == viewDataSourcesID) {
        collectionFields.push(field);
      }
    });
    return collectionFields;
  }

  private static getCollectionFileFields(form: CollectionForm, sourceCollectionsID: number, viewDataSourcesID: number): CollectionFormField[] {
    const collectionFileFields: CollectionFormField[] = [];

    const fileProtectedFieldTypes = [ProtectedFieldType.File, ProtectedFieldType.Extension, ProtectedFieldType.Size, ProtectedFieldType.VersionDate, ProtectedFieldType.Version,
      ProtectedFieldType.ChangeReason, ProtectedFieldType.Title, ProtectedFieldType.DocumentStatus, ProtectedFieldType.DocumentCheckinStatus, ProtectedFieldType.DocumentFlow,
      ProtectedFieldType.MinorVersion, ProtectedFieldType.MajorVersion];

    CollectionFormService.extractFieldsFromForm(form).forEach(field => {
      if (fileProtectedFieldTypes.includes(field.ProtectedFieldType) && field.SourceCollectionsID == sourceCollectionsID && field.DataDesignViewDataSourcesID == viewDataSourcesID) {
        collectionFileFields.push(field);
      }
    });
    return collectionFileFields;
  }

  public static getScriptingFieldActions(formId: string, form: CollectionForm, field: FormScriptingField, evaluationAction: EvaluationActionDto): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    scriptingActions = scriptingActions.concat(FormScriptingActionsService.getScriptingActionsForEvaluationAction(formId, form, field, evaluationAction.Values));
    return scriptingActions;
  }

  /**
   * Executes an action (an effect from scripting). <br>
   * Currently supported actions are: SetVisible, SetInvisible, SetRequired, SetNotRequired
   * @param formId
   * @param form
   * @param field
   * @param actionValues
   */
  private static getScriptingActionsForEvaluationAction(formId: string, form: CollectionForm, field: FormScriptingField, actionValues: EvaluationActionFieldValueDto[]): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    actionValues.forEach(actionValue => {
      switch (actionValue.ActionsType) {
        case EvaluationsActionsType.SetVisible:
          scriptingActions = scriptingActions.concat(FormScriptingActionsService.getFieldVisibilityActions(formId, field, form, true));
          break;
        case EvaluationsActionsType.SetInvisible:
          scriptingActions = scriptingActions.concat(FormScriptingActionsService.getFieldVisibilityActions(formId, field, form, false));
          break;
        case EvaluationsActionsType.SetRequired:
          scriptingActions = scriptingActions.concat(FormScriptingActionsService.getFieldRequiredActions(formId, field, true));
          break;
        case EvaluationsActionsType.SetNotRequired:
          scriptingActions = scriptingActions.concat(FormScriptingActionsService.getFieldRequiredActions(formId, field, false));
          break;
        default:
          break;
      }
    });
    return scriptingActions;
  }

  private static getAllCollectionFileFieldsVisibilityActions(formId: string, form: CollectionForm, sourceCollectionsID: number, ViewDataSourcesID: number, hide: boolean): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    const fileFields = FormScriptingActionsService.getCollectionFileFields(form, sourceCollectionsID, ViewDataSourcesID);

    fileFields.forEach(field => {
      //field.IsHidden = hide;
      scriptingActions.push({
        formId: formId,
        fieldId: field.Id,
        property: 'IsHidden',
        value: hide
      });
    });
    return scriptingActions;
  }

  private static getAllCollectionFieldsVisibilityActions(formId: string, form: CollectionForm, sourceCollectionsID: number, ViewDataSourcesID: number, hide: boolean): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    const fields = FormScriptingActionsService.getSourceCollectionFields(form, sourceCollectionsID, ViewDataSourcesID);
    fields.forEach(field => {
      scriptingActions.push({
        formId: formId,
        fieldId: field.Id,
        property: 'IsHidden',
        value: hide
      });
    });
    return scriptingActions;
  }

  /**
   * Sets the IsHidden property. Will do nothing if IsRequired is true and isVisible is false.
   * (Required fields cannot be hidden)
   * @param formId
   * @param {FormScriptingField} field
   * @param {CollectionForm} form
   * @param {boolean} isVisible
   * @private
   */
  private static getFieldVisibilityActions(formId: string, field: FormScriptingField, form: CollectionForm, isVisible: boolean): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    scriptingActions.push({
      formId: formId,
      fieldId: field.field.Id,
      property: 'IsHidden',
      value: !isVisible
    });

    if (field.field.LinkedCollectionStorageType == LinkedCollectionStorageType.Relational && field.field.IsLookupField) {
      console.warn('Relational linked collection with action on lookup field: Set all linked collection fields invisible');
      scriptingActions = scriptingActions.concat(this.getAllCollectionFieldsVisibilityActions(formId, form, field.field.SourceCollectionsID, field.field.ViewDataSourcesID, !isVisible));
    }

    if (field.field.ProtectedFieldType == ProtectedFieldType.File) {
      console.warn('Action on file field: Set all linked collection fields invisible');
      scriptingActions = scriptingActions.concat(FormScriptingActionsService.getAllCollectionFileFieldsVisibilityActions(formId, form, field.field.SourceCollectionsID, field.field.ViewDataSourcesID, !isVisible));
    }

    return scriptingActions;
  }

  /**
   * Sets the field IsRequired property. If true, also sets IsHidden to false.
   * @param {FormScriptingField} field
   * @param {boolean} isRequired
   * @private
   */
  private static getFieldRequiredActions(formId: string, field: FormScriptingField, isRequired: boolean): ScriptingAction[] {
    let scriptingActions: ScriptingAction[] = [];
    //console.debug(`Setting field required for field ${field.field.Caption} to ${isRequired}`);
    scriptingActions.push({
      formId: formId,
      fieldId: field.field.Id,
      property: 'IsRequired',
      value: isRequired
    });
    if (isRequired)
      scriptingActions.push({
        formId: formId,
        fieldId: field.field.Id,
        property: 'IsHidden',
        value: false
      });
    return scriptingActions;
  }
}
